import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from 'Context';
import { useNavigate, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { Page } from 'Components';
import { Pathname } from 'Routes';
import PillNav from 'Components/reactbits/PillNav';
import { UserRoundPlus, Copy, Check } from 'Utils/lucideIcons';
import styles from './index.module.css';

// Viewers / Groups / Successors are sibling routes; the PillNav switches between
// them (active tab derived from the current path).
const TAB_ROUTES = [
  Pathname.getViewersPath(Pathname.viewers.viewer),
  Pathname.getViewersPath(Pathname.viewers.groups),
  Pathname.getViewersPath(Pathname.viewers.successors),
  Pathname.getViewersPath(Pathname.viewers.connectedAccounts),
];

export const ViewerPage = ({
  children, title, addActionName, link, onAddAction, subTitle, restricted, searchElement,
  showCopy, onCopyClick, hideHeaderActions,
}) => {
  const { langText, setTopOrBottom } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    setTopOrBottom(true);
    return () => {
      setTopOrBottom(false); // reset when leaving
    };
  }, []);
  const sideBarText = langText?.viewersSideBar;
  const [copied, setCopied] = useState(false);

  const handleCopyClick = () => {
    onCopyClick?.();
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const tabLabels = [
    sideBarText?.viewersSidebarTitle || 'Viewers',
    sideBarText?.groupSidebarTitle || 'Groups',
    sideBarText?.successorSidebarTitle || 'Successors',
    'Connected Accounts',
  ];
  const activeIndex = Math.max(0, TAB_ROUTES.indexOf(location.pathname));
  const subtext = subTitle
    || 'Manage who can watch your projector — invite viewers, organize them into groups, and set successors.';

  return (
    <Page
      contentClassName={restricted ? styles.pageContentRes : styles.pageContent}
      headerTheme={styles.headerTheme}
    >
      <div className={styles.viewerPage}>
        {/* Header: title + subtext. The action buttons live down in the tab row
            so the primary action sits on the same line as the pill tabs. */}
        <div className={styles.viewerHead}>
          <div className={styles.titleBlock}>
            <h1 className={cn(styles.pageTitle, { [styles.restrictedPageTitle]: restricted })}>
              {title}
            </h1>
            <p className={styles.pageSub}>{subtext}</p>
          </div>
        </div>

        {/* Pill tab bar (route-driven) + optional search and the page actions
            on the right, all on one horizontal line. */}
        <div className={styles.tabBar}>
          <PillNav
            items={tabLabels}
            activeIndex={activeIndex}
            onChange={(i) => navigate(TAB_ROUTES[i])}
            accent="#15171c"
          />
          <div className={styles.tabBarRight}>
            {searchElement && <div className={styles.searchElementWrapper}>{searchElement}</div>}
            {!hideHeaderActions && (
              <div className={styles.headerActions}>
                <button
                  type="button"
                  className={cn(styles.inviteBtn, { [styles.disabledbutton]: restricted })}
                  onClick={() => {
                    if (restricted) return;
                    // Pages that own their own creation modal pass a handler;
                    // the rest still navigate to a `?…` search-param route.
                    if (onAddAction) onAddAction();
                    else if (link) navigate(link);
                  }}
                  disabled={restricted}
                >
                  <UserRoundPlus size={17} strokeWidth={2} />
                  <span>{addActionName || 'Invite'}</span>
                </button>
                {showCopy && (
                  <button
                    type="button"
                    className={cn(styles.copyLinkBtn, { [styles.copyLinkBtnCopied]: copied })}
                    onClick={handleCopyClick}
                    title="Copy invite link"
                  >
                    {copied ? <Check size={16} strokeWidth={2.4} /> : <Copy size={16} strokeWidth={2} />}
                    <span>{copied ? 'Copied' : 'Copy link'}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Data table / list. */}
        <div className={styles.content}>{children}</div>
      </div>
    </Page>
  );
};
